import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useCreateWeightLog } from '../hooks/useWeightLogs.js';
import { cleanupStaleDismissKeys, useWeightPromptGate } from '../hooks/useWeightPromptGate.js';
import { toast } from '../lib/toast.js';

import { WeightPromptModal } from './WeightPromptModal.js';

/**
 * Glue component that wires the daily-weight prompt: gate hook decides if
 * the prompt should show, modal renders the UI, useCreateWeightLog handles
 * the mutation. Mounted once in the authenticated Layout so the modal
 * appears on every fresh shell mount (login, hard reload, public→auth nav)
 * but NOT on internal route changes (Layout stays mounted while Outlet
 * children swap).
 *
 * Open state is latched in local state: once the gate flips shouldShow to
 * true, we open the modal and keep it open until the user logs or
 * dismisses. We do not close the modal just because shouldShow becomes
 * false mid-interaction — the user already engaged.
 */
export function WeightPromptGate() {
  const { shouldShow, dismiss } = useWeightPromptGate();
  const createMutation = useCreateWeightLog();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Don't interrupt the training flow — the prompt would pop over the Start
  // Workout / active-session / routines / history screens. It still opens on
  // any other route once the user leaves the Train surface.
  const onTrainRoute = ['/workout', '/routines', '/history'].some((p) =>
    location.pathname.startsWith(p),
  );

  useEffect(() => {
    cleanupStaleDismissKeys();
  }, []);

  useEffect(() => {
    if (shouldShow && !open && !onTrainRoute) setOpen(true);
    // Intentionally one-way: gate → modal. Closing is driven by user action
    // (onLog / onDismiss), not by shouldShow flipping back to false.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShow, onTrainRoute]);

  async function handleLog(weightKg: number): Promise<void> {
    await createMutation.mutateAsync({ weightKg });
    toast.success('Logged');
    setOpen(false);
  }

  function handleDismiss(): void {
    dismiss();
    setOpen(false);
  }

  return <WeightPromptModal open={open} onLog={handleLog} onDismiss={handleDismiss} />;
}
